import { useCallback, useEffect, useState } from "react";
import {
  useGetDonationsTenantConfigQuery,
  useUpdateDonationsTenantConfigMutation,
} from "../../../state/domainRouterApi";
import { ModuleSectionNav } from "../../../components/navigation/ModuleSectionNav";
import { YstRouteTransition } from "../../../components/motion/YstRouteTransition";
import { SupportErrorMessage } from "../../../components/overlays/EngineeringSupportBanner";
import { DONATIONS_SECTION_ITEMS } from "./sectionNav";

const PRIMARY = "var(--primary)";
const TEXT = "var(--iron-grey)";
const BORDER = "var(--border)";

function LoadingSkeleton() {
  return (
    <div className="space-y-4">
      {[1, 2, 3, 4, 5].map((index) => (
        <div key={index} className="h-14 animate-pulse rounded-lg bg-muted" />
      ))}
    </div>
  );
}

function ErrorBanner({ message }: { message: string }) {
  return <SupportErrorMessage>{message}</SupportErrorMessage>;
}

type SettingsFormState = {
  latitude: string;
  longitude: string;
  timezone: string;
  elevation: string;
  receiptOrgName: string;
  receiptOrgAddress: string;
  receiptTaxId: string;
  feeRecoveryEnabled: boolean;
  feeRecoveryPercent: string;
  moderationDefault: string;
};

const EMPTY_SETTINGS: SettingsFormState = {
  latitude: "",
  longitude: "",
  timezone: "America/New_York",
  elevation: "",
  receiptOrgName: "",
  receiptOrgAddress: "",
  receiptTaxId: "",
  feeRecoveryEnabled: false,
  feeRecoveryPercent: "3",
  moderationDefault: "manual_review",
};

const MODERATION_OPTIONS = [
  { value: "manual_review", label: "Require manual approval" },
  { value: "auto_approve", label: "Auto-approve all dedications" },
];

const inputClasses = "yst-control-input block w-full px-3 py-2.5 text-sm";

function FormField({
  label,
  htmlFor,
  hint,
  children,
}: {
  label: string;
  htmlFor: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <div>
      <label
        htmlFor={htmlFor}
        className="mb-1.5 block text-sm font-medium"
        style={{ color: TEXT }}
      >
        {label}
      </label>
      {hint ? (
        <p className="mb-1 text-xs" style={{ color: `${TEXT}88` }}>
          {hint}
        </p>
      ) : null}
      {children}
    </div>
  );
}

export function SchoolDonationsSettingsPage() {
  const { data, isLoading, isError, error } =
    useGetDonationsTenantConfigQuery();
  const [updateConfig, { isLoading: saving }] =
    useUpdateDonationsTenantConfigMutation();

  const [form, setForm] = useState<SettingsFormState>(EMPTY_SETTINGS);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saveSuccess, setSaveSuccess] = useState(false);

  useEffect(() => {
    if (!data || typeof data !== "object") return;
    const config = data as Record<string, unknown>;
    const geo = (config.geo ?? {}) as Record<string, unknown>;
    const receiptDefaults = (config.receiptDefaults ?? {}) as Record<
      string,
      unknown
    >;
    const feeRecovery = (config.feeRecovery ?? {}) as Record<string, unknown>;
    setForm({
      latitude: geo.latitude != null ? String(geo.latitude) : "",
      longitude: geo.longitude != null ? String(geo.longitude) : "",
      timezone: String(geo.timezone ?? "America/New_York"),
      elevation: geo.elevationMeters != null ? String(geo.elevationMeters) : "",
      receiptOrgName: String(receiptDefaults.orgName ?? ""),
      receiptOrgAddress: String(receiptDefaults.orgAddress ?? ""),
      receiptTaxId: String(receiptDefaults.taxId ?? ""),
      feeRecoveryEnabled: Boolean(feeRecovery.enabled),
      feeRecoveryPercent:
        feeRecovery.defaultPercent != null
          ? String(feeRecovery.defaultPercent)
          : "3",
      moderationDefault: String(config.moderationDefault ?? "manual_review"),
    });
  }, [data]);

  const handleChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
      const { name, value, type } = event.target;
      const checked =
        type === "checkbox"
          ? (event.target as HTMLInputElement).checked
          : false;
      setForm((current) => ({
        ...current,
        [name]: type === "checkbox" ? checked : value,
      }));
      setSaveError(null);
      setSaveSuccess(false);
    },
    [],
  );

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setSaveError(null);
    setSaveSuccess(false);

    const input: Record<string, unknown> = {
      geo: {
        timezone: form.timezone,
      },
      moderationDefault: form.moderationDefault,
      feeRecovery: {
        enabled: form.feeRecoveryEnabled,
      },
      receiptDefaults: {
        orgName: form.receiptOrgName.trim() || undefined,
        orgAddress: form.receiptOrgAddress.trim() || undefined,
        taxId: form.receiptTaxId.trim() || undefined,
      },
    };

    const lat = Number.parseFloat(form.latitude);
    if (Number.isFinite(lat))
      (input.geo as Record<string, unknown>).latitude = lat;

    const lng = Number.parseFloat(form.longitude);
    if (Number.isFinite(lng))
      (input.geo as Record<string, unknown>).longitude = lng;

    const elev = Number.parseFloat(form.elevation);
    if (Number.isFinite(elev))
      (input.geo as Record<string, unknown>).elevationMeters = elev;

    const feePercent = Number.parseFloat(form.feeRecoveryPercent);
    if (Number.isFinite(feePercent) && feePercent > 0) {
      (input.feeRecovery as Record<string, unknown>).defaultPercent =
        feePercent;
    }

    try {
      await updateConfig(input).unwrap();
      setSaveSuccess(true);
    } catch (err) {
      const message =
        err instanceof Error
          ? err.message
          : err && typeof err === "object" && "message" in err
            ? String((err as { message?: unknown }).message)
            : "Failed to save settings. The donations backend may not be deployed.";
      setSaveError(message);
    }
  };

  return (
    <main
      data-testid="school-donations-settings"
      data-route-ready="true"
      data-extracted-from="source-faithful:school-donations-v1.0"
      className="min-h-screen bg-[var(--background-light)]"
    >
      <YstRouteTransition className="mx-auto max-w-[1440px] space-y-6">
        <div>
          <h2 className="yst-surface-title">Donation Settings</h2>
        </div>

        <ModuleSectionNav
          items={DONATIONS_SECTION_ITEMS}
          activeId="settings"
          ariaLabel="Donations sections"
          desktopClassName="source-faithful-tabs"
        />

        {isLoading ? (
          <LoadingSkeleton />
        ) : isError ? (
          <ErrorBanner
            message={
              (error && typeof error === "object" && "message" in error
                ? String((error as { message?: unknown }).message)
                : null) ??
              "Settings data is not yet available. The donations backend has not been deployed."
            }
          />
        ) : (
          <form
            onSubmit={handleSubmit}
            className="yst-surface-panel space-y-8 p-6"
          >
            <fieldset className="space-y-4">
              <legend
                className="text-sm font-semibold uppercase tracking-wider"
                style={{ color: TEXT }}
              >
                Location & Zmanim
              </legend>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
                <FormField
                  label="Latitude"
                  htmlFor="latitude"
                  hint="e.g. 40.7128"
                >
                  <input
                    id="latitude"
                    name="latitude"
                    type="text"
                    inputMode="decimal"
                    value={form.latitude}
                    onChange={handleChange}
                    className={inputClasses}
                    style={{ borderColor: BORDER, color: TEXT }}
                    placeholder="40.7128"
                  />
                </FormField>
                <FormField
                  label="Longitude"
                  htmlFor="longitude"
                  hint="e.g. -74.0060"
                >
                  <input
                    id="longitude"
                    name="longitude"
                    type="text"
                    inputMode="decimal"
                    value={form.longitude}
                    onChange={handleChange}
                    className={inputClasses}
                    style={{ borderColor: BORDER, color: TEXT }}
                    placeholder="-74.0060"
                  />
                </FormField>
                <FormField label="Timezone" htmlFor="timezone">
                  <input
                    id="timezone"
                    name="timezone"
                    type="text"
                    value={form.timezone}
                    onChange={handleChange}
                    className={inputClasses}
                    style={{ borderColor: BORDER, color: TEXT }}
                    placeholder="America/New_York"
                  />
                </FormField>
                <FormField label="Elevation (m)" htmlFor="elevation">
                  <input
                    id="elevation"
                    name="elevation"
                    type="text"
                    inputMode="decimal"
                    value={form.elevation}
                    onChange={handleChange}
                    className={inputClasses}
                    style={{ borderColor: BORDER, color: TEXT }}
                    placeholder="10"
                  />
                </FormField>
              </div>
            </fieldset>

            <fieldset className="space-y-4">
              <legend
                className="text-sm font-semibold uppercase tracking-wider"
                style={{ color: TEXT }}
              >
                Receipt Defaults
              </legend>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <FormField label="Organization Name" htmlFor="receiptOrgName">
                  <input
                    id="receiptOrgName"
                    name="receiptOrgName"
                    type="text"
                    value={form.receiptOrgName}
                    onChange={handleChange}
                    className={inputClasses}
                    style={{ borderColor: BORDER, color: TEXT }}
                    placeholder="Yeshiva Shaarei Tzion"
                  />
                </FormField>
                <FormField label="Tax ID / EIN" htmlFor="receiptTaxId">
                  <input
                    id="receiptTaxId"
                    name="receiptTaxId"
                    type="text"
                    value={form.receiptTaxId}
                    onChange={handleChange}
                    className={inputClasses}
                    style={{ borderColor: BORDER, color: TEXT }}
                    placeholder="XX-XXXXXXX"
                  />
                </FormField>
              </div>
              <FormField
                label="Organization Address"
                htmlFor="receiptOrgAddress"
              >
                <input
                  id="receiptOrgAddress"
                  name="receiptOrgAddress"
                  type="text"
                  value={form.receiptOrgAddress}
                  onChange={handleChange}
                  className={inputClasses}
                  style={{ borderColor: BORDER, color: TEXT }}
                  placeholder="123 Main Street, Newark, NJ 07101"
                />
              </FormField>
            </fieldset>

            <fieldset className="space-y-4">
              <legend
                className="text-sm font-semibold uppercase tracking-wider"
                style={{ color: TEXT }}
              >
                Fee Recovery
              </legend>
              <div className="flex items-center gap-4">
                <label
                  className="flex items-center gap-2 text-sm font-medium"
                  style={{ color: TEXT }}
                >
                  <input
                    id="feeRecoveryEnabled"
                    name="feeRecoveryEnabled"
                    type="checkbox"
                    checked={form.feeRecoveryEnabled}
                    onChange={handleChange}
                    className="h-4 w-4 rounded"
                    style={{ accentColor: PRIMARY }}
                  />
                  Enable fee recovery
                </label>
                {form.feeRecoveryEnabled ? (
                  <div className="flex items-center gap-2">
                    <input
                      id="feeRecoveryPercent"
                      name="feeRecoveryPercent"
                      type="number"
                      min="0"
                      max="100"
                      step="0.1"
                      value={form.feeRecoveryPercent}
                      onChange={handleChange}
                      className="w-20 rounded-lg border px-3 py-2 text-sm"
                      style={{ borderColor: BORDER, color: TEXT }}
                    />
                    <span className="text-sm" style={{ color: `${TEXT}88` }}>
                      %
                    </span>
                  </div>
                ) : null}
              </div>
            </fieldset>

            <fieldset className="space-y-4">
              <legend
                className="text-sm font-semibold uppercase tracking-wider"
                style={{ color: TEXT }}
              >
                Moderation
              </legend>
              <FormField
                label="Default moderation policy"
                htmlFor="moderationDefault"
              >
                <select
                  id="moderationDefault"
                  name="moderationDefault"
                  value={form.moderationDefault}
                  onChange={handleChange}
                  className={inputClasses}
                  style={{ borderColor: BORDER, color: TEXT }}
                >
                  {MODERATION_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </FormField>
            </fieldset>

            {saveError ? <ErrorBanner message={saveError} /> : null}

            {saveSuccess ? (
              <div className="rounded-xl border border-aquamarine/30 bg-aquamarine/10 px-6 py-4 text-sm font-medium text-jungle">
                Settings saved successfully.
              </div>
            ) : null}

            <button
              type="submit"
              disabled={saving}
              className="yst-button-primary flex items-center justify-center px-6 py-3 text-sm font-semibold disabled:cursor-not-allowed disabled:opacity-60"
            >
              {saving ? "Saving..." : "Save Settings"}
            </button>
          </form>
        )}
      </YstRouteTransition>
    </main>
  );
}
